import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Switch } from 'antd';
import { Trophy, CheckCircle2, XCircle, Package, ImageOff, Loader2 } from 'lucide-react';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useCollection } from '../../hooks/useCollection';
import { PokemonCard } from '../../types/pokemon';
import { useMissionProgress } from '../../hooks/useMissionProgress';
import { PageLayout } from '../../components/layout/PageLayout';
import { MissionDetailSkeleton } from '../../components/skeletons/MissionDetailSkeleton';
import { useTranslation } from '../../hooks/useTranslation';

interface MissionCardVariant {
  card_id: number;
  cards: PokemonCard;
}

interface MissionCardGroup {
  id: string;
  mission_card_variants: MissionCardVariant[];
}

interface MissionReward {
  quantity: number;
  rewards: {
    id: string;
    name: string;
    image_url: string;
  };
}

interface SupabaseMission {
  id: string;
  name: string;
  description: string;
  mission_card_groups: MissionCardGroup[];
  mission_rewards: MissionReward[];
  sets: {
    id: string;
    name: string;
    display_name: string;
    display_name_en?: string;
  };
}

interface PackRecommendation {
  pack_id: string;
  name: string;
  missing_cards: number;
  img: string | null;
  total_cards: number;
}

interface PackInfo {
  id: string;
  name: string;
  img: string | null;
}

export function MissionDetail() {
  const { code, missionId } = useParams<{ code: string; missionId: string }>();
  const navigate = useNavigate();
  const { t, lang, getPathForLang } = useTranslation();
  const [mission, setMission] = useState<SupabaseMission | null>(null);
  const [loading, setLoading] = useState(true);
  const { collection } = useCollection();
  const { calculateProgress } = useMissionProgress();
  const [packRecommendations, setPackRecommendations] = useState<PackRecommendation[]>([]);
  const [packsInfo, setPacksInfo] = useState<Record<string, PackInfo>>({});
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);

  // État pour le préchargement des images des packs
  const [packImagesStatus, setPackImagesStatus] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});

  useEffect(() => {
    loadMission();
  }, [missionId]);

  useEffect(() => {
    if (mission && Object.keys(packsInfo).length > 0) {
      calculatePackRecommendations();
    }
  }, [mission, collection, packsInfo]);

  const loadMission = async () => {
    try {
      const { data, error } = await supabase
        .from('missions')
        .select(`
          *,
          mission_card_groups (
            id,
            mission_card_variants (
              card_id,
              cards (
                id,
                card_fr,
                card_en,
                image,
                pack
              )
            )
          ),
          mission_rewards (
            quantity,
            rewards (*)
          ),
          sets (
            id,
            name,
            display_name
          )
        `)
        .eq('id', missionId)
        .single();

      if (error) throw error;
      setMission(data);

      // Charger les informations des packs
      if (data) {
        const packs = new Set<string>();
        data.mission_card_groups.forEach((group: MissionCardGroup) => {
          group.mission_card_variants.forEach((variant: MissionCardVariant) => {
            if (variant.cards.pack) {
              packs.add(variant.cards.pack);
            }
          });
        });

        const { data: packsData, error: packsError } = await supabase
          .from('packs')
          .select('id, name, img')
          .in('name', Array.from(packs));

        if (packsError) throw packsError;

        // Créer le Record des packs et précharger les images immédiatement
        const packsRecord: Record<string, PackInfo> = {};
        packsData?.forEach(pack => {
          packsRecord[pack.name] = pack;
          if (pack.img) {
            // Précharger l'image dès qu'on reçoit les données du pack
            setPackImagesStatus(prev => ({ ...prev, [pack.name]: 'loading' }));
            const img = new Image();
            img.src = `${SUPABASE_IMAGE_BASE_URL}${pack.img}`;
            img.onload = () => {
              setPackImagesStatus(prev => ({ ...prev, [pack.name]: 'loaded' }));
            };
            img.onerror = () => {
              setPackImagesStatus(prev => ({ ...prev, [pack.name]: 'error' }));
            };
          }
        });
        setPacksInfo(packsRecord);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la mission:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePackRecommendations = () => {
    if (!mission) return;

    console.log('PacksInfo disponible:', packsInfo);

    const packStats: Record<string, PackRecommendation> = {};

    // Première passe : compter le nombre total de cartes par pack
    mission.mission_card_groups.forEach(group => {
      group.mission_card_variants.forEach(variant => {
        const pack = variant.cards.pack;
        if (pack) {
          if (!packStats[pack]) {
            const packInfo = packsInfo[pack];
            console.log(`Création stats pour pack ${pack}:`, {
              packInfo,
              img: packInfo?.img
            });
            packStats[pack] = {
              pack_id: pack,
              name: packInfo?.name || pack,
              missing_cards: 0,
              total_cards: 0,
              img: packInfo?.img || null
            };
          }
          packStats[pack].total_cards++;
        }
      });
    });

    // Deuxième passe : compter les cartes manquantes
    mission.mission_card_groups.forEach(group => {
      const hasOneVariant = group.mission_card_variants.some(variant => {
        const userCard = collection[variant.card_id];
        return userCard && userCard.quantity > 0;
      });

      if (!hasOneVariant) {
        group.mission_card_variants.forEach(variant => {
          const pack = variant.cards.pack;
          if (pack && packStats[pack]) {
            packStats[pack].missing_cards++;
          }
        });
      }
    });

    const recommendations = Object.values(packStats)
      .filter(pack => pack.name && pack.missing_cards > 0)  // Ne garder que les packs avec des cartes manquantes
      .sort((a, b) => b.missing_cards - a.missing_cards);

    console.log('Recommendations calculées:', recommendations);
    console.log('État des images:', packImagesStatus);

    setPackRecommendations(recommendations);
  };

  const progress = mission ? calculateProgress(mission, collection) : 0;

  if (loading || !mission) {
    return (
      <PageLayout
        breadcrumbProps={{
          setName: t('mission_detail.loading'),
          missionName: t('mission_detail.loading')
        }}
      >
        <MissionDetailSkeleton />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      breadcrumbProps={{
        setName: lang === 'en' ? mission.sets?.display_name_en || mission.sets?.display_name : mission.sets?.display_name,
        missionName: mission.name
      }}
    >
      {/* En-tête de la Mission */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="flex-shrink-0">
            <Trophy className="w-12 h-12 text-indigo-600" />
          </div>
          <div className="flex-grow">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{mission.name}</h1>
            <p className="mt-2 text-gray-600">{mission.description}</p>
            <div className="mt-4 flex items-center gap-3">
              <div className="text-sm font-medium text-gray-500">
                {t('mission_detail.progress')}: {progress}%
              </div>
              <div className="flex-grow max-w-xs h-2 bg-gray-200 rounded-full">
                <div 
                  className="h-2 bg-indigo-600 rounded-full" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6 lg:auto-rows-fr">
        {/* Section Packs Recommandés */}
        {packRecommendations.length > 0 && (
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md h-full flex flex-col">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" />
              {t('mission_detail.recommended_pack')}
            </h2>

            {/* Pack le plus recommandé */}
            <div className="flex-grow bg-gradient-to-r from-indigo-50 to-gray-50 rounded-lg p-4 flex items-center">
              <div className="flex items-center gap-4 w-full">
                <div className="flex-shrink-0 w-20 h-20 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  {packRecommendations[0]?.img && packImagesStatus[packRecommendations[0].pack_id] === 'loaded' ? (
                    <img
                      src={`${SUPABASE_IMAGE_BASE_URL}${packRecommendations[0].img}`}
                      alt={packRecommendations[0].name}
                      className="w-18 h-18 object-contain"
                    />
                  ) : packImagesStatus[packRecommendations[0]?.pack_id] === 'loading' ? (
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  ) : (
                    <Package className="w-10 h-10 text-gray-400" />
                  )}
                </div>
                <div className="flex-grow min-w-0">
                  <h3 className="font-medium text-gray-900">{packRecommendations[0].name}</h3>
                  <div className="text-sm text-gray-500">
                    {packRecommendations[0].total_cards - packRecommendations[0].missing_cards} / {packRecommendations[0].total_cards} {t('mission_detail.cards')}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 rounded-full"
                        style={{ width: `${((packRecommendations[0].total_cards - packRecommendations[0].missing_cards) / packRecommendations[0].total_cards) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-red-500 font-medium whitespace-nowrap">
                      {packRecommendations[0].missing_cards} {t('mission_detail.missing_cards')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Récompenses */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md h-full flex flex-col">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-indigo-600" />
            {t('mission_detail.rewards')}
          </h2>
          <div className="flex-grow flex flex-wrap gap-4 items-center justify-center content-center">
            {mission.mission_rewards.map((reward, index) => (
              <div key={index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                <img 
                  src={`${SUPABASE_IMAGE_BASE_URL}${reward.rewards.image_url}`} 
                  alt={reward.rewards.name}
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <div className="font-medium">{reward.rewards.name}</div>
                  <div className="text-sm text-gray-500">×{reward.quantity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section des autres packs recommandés */}
      {packRecommendations.length > 1 && (
        <div className="mt-6 bg-white rounded-xl p-4 sm:p-6 shadow-md">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            {t('mission_detail.other_recommended_packs')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {packRecommendations.slice(1).map((pack) => (
              <div 
                key={`pack-${pack.pack_id}`}
                className="flex items-center gap-4 bg-gray-50 rounded-lg p-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                  {pack.img && packImagesStatus[pack.pack_id] === 'loaded' ? (
                    <img
                      src={`${SUPABASE_IMAGE_BASE_URL}${pack.img}`}
                      alt={pack.name}
                      className="w-10 h-10 object-contain"
                    />
                  ) : packImagesStatus[pack.pack_id] === 'loading' ? (
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  ) : (
                    <Package className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-medium text-gray-900">{pack.name}</h3>
                    <div className="text-sm text-gray-500">
                      {pack.total_cards - pack.missing_cards} / {pack.total_cards}
                    </div>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 rounded-full"
                        style={{ width: `${((pack.total_cards - pack.missing_cards) / pack.total_cards) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-red-500 whitespace-nowrap">
                      ({pack.missing_cards} {t('mission_detail.missing')})
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section Cartes Requises */}
      <div className="mt-6 bg-white rounded-xl p-4 sm:p-6 shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{t('mission_detail.required_cards')}</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('mission_detail.show_only_missing')}</span>
            <Switch
              checked={showOnlyMissing}
              onChange={setShowOnlyMissing}
              className="bg-gray-200"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {mission.mission_card_groups
            .filter(group => {
              if (!showOnlyMissing) return true;
              const hasOneVariant = group.mission_card_variants.some(variant => {
                const userCard = collection[variant.card_id];
                return userCard && userCard.quantity > 0;
              });
              return !hasOneVariant;
            })
            .map((group: MissionCardGroup) => {
              const mainVariant = group.mission_card_variants[0];
              const otherVariants = group.mission_card_variants.slice(1);
              const mainCard = mainVariant.cards;
              const mainCardInCollection = collection[mainCard.id];
              const isMainCardOwned = mainCardInCollection && mainCardInCollection.quantity > 0;
              
              // Vérifier si au moins une carte du groupe est possédée
              const hasOneVariant = group.mission_card_variants.some(variant => {
                const userCard = collection[variant.card_id];
                return userCard && userCard.quantity > 0;
              });

              const cardName = lang === 'en' ? (mainCard.card_en || mainCard.card_fr) : mainCard.card_fr;

              return (
                <div 
                  key={group.id} 
                  className="bg-white rounded-lg shadow-sm p-4 relative"
                >
                  {/* Indicateur de statut */}
                  <div className="absolute -top-2 -right-2 z-10">
                    {hasOneVariant ? (
                      <div className="bg-white rounded-full p-0.5 shadow-sm">
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                      </div>
                    ) : (
                      <div className="bg-white rounded-full p-0.5 shadow-sm">
                        <XCircle className="w-6 h-6 text-red-500" />
                      </div>
                    )}
                  </div>

                  <div className={`${otherVariants.length === 0 ? 'flex justify-center' : 'flex gap-4'}`}>
                    {/* Carte principale */}
                    <div
                      onClick={() => navigate(getPathForLang(`/sets/${code}/cards/${mainCard.id}`))}
                      className="relative flex-shrink-0 w-[200px] cursor-pointer"
                    >
                      <div className="relative aspect-[3/4]">
                        {mainCard.image ? (
                          <img
                            src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${mainCard.image}`}
                            alt={cardName || t('mission_detail.pokemon_card')}
                            className={`w-full h-full object-contain ${!isMainCardOwned ? 'grayscale' : ''}`}
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                            <ImageOff className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <div className="absolute top-1 right-1">
                          {isMainCardOwned ? (
                            <div className="flex items-center bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded">
                              {mainCardInCollection?.quantity || 0}
                            </div>
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      </div>
                      <div className="mt-2 space-y-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {cardName}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {mainCard.set}
                          </span>
                          <span className="text-xs font-medium bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-full">
                            {mainCard.rarity}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Variantes */}
                    {otherVariants.length > 0 && (
                      <div className="flex flex-col gap-2 flex-shrink-0" style={{ width: '140px' }}>
                        {otherVariants.map(variant => {
                          const card = variant.cards;
                          const cardInCollection = collection[card.id];
                          const isOwned = cardInCollection && cardInCollection.quantity > 0;
                          const variantCardName = lang === 'en' ? (card.card_en || card.card_fr) : card.card_fr;

                          return (
                            <div
                              key={card.id}
                              onClick={() => navigate(getPathForLang(`/sets/${code}/cards/${card.id}`))}
                              className="relative flex items-center bg-gray-50 rounded-lg p-2 cursor-pointer hover:bg-indigo-50 transition-colors w-full"
                            >
                              <div className="relative w-12 h-12 flex-shrink-0">
                                {card.image ? (
                                  <img
                                    src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${card.image}`}
                                    alt={variantCardName || t('mission_detail.pokemon_card')}
                                    className={`w-full h-full object-contain ${!isOwned ? 'grayscale' : ''}`}
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                                    <ImageOff className="w-4 h-4 text-gray-400" />
                                  </div>
                                )}
                                <div className="absolute -top-1 -right-1">
                                  {isOwned ? (
                                    <div className="flex items-center bg-green-100 text-green-800 text-xs font-medium px-1.5 py-0.5 rounded-full">
                                      {cardInCollection?.quantity || 0}
                                    </div>
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-500" />
                                  )}
                                </div>
                              </div>
                              <div className="ml-2 min-w-0 w-full">
                                <div className="text-xs font-medium text-gray-900 truncate">
                                  {variantCardName}
                                </div>
                                <div className="flex items-center justify-between w-full">
                                  <span className="text-[10px] text-gray-500 truncate max-w-[60px]">
                                    {card.set}
                                  </span>
                                  <span className="text-[10px] font-medium bg-indigo-100 text-indigo-800 px-1 py-0.5 rounded-full">
                                    {card.rarity}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </PageLayout>
  );
}